import React, { useRef } from "react";
import { Search, X } from "lucide-react";
import { XIcon } from "@/shared/components/icons/animated";
import PixelAvatar from "./PixelAvatar";
import Logo from "./Logo";
import { agentMood } from "@/shared/lib/agent-mood";

import type { Agent } from "@/shared/types";
import type { AnimatedIconHandle } from "@/shared/components/icons/animated";

/* Shared UI primitives — one source of truth for chrome repeated across pages.
   Sizes and tones here are canonical. Pass className for layout only (margins,
   width, flex placement) — never to re-style type scale, colors, or rounding. */

/* Search box. Default tone sits on canvas/surface backgrounds; `raised` is for
   sunk backgrounds like the sidebar rail. Width via className (e.g. w-[220px]). */
export function SearchInput({ raised = false, className = "", inputRef, ...rest }:
  React.ComponentPropsWithoutRef<"input"> & { raised?: boolean; inputRef?: React.Ref<HTMLInputElement> }
) {
  return (
    <div
      className={`flex items-center gap-2 px-2.5 h-8 rounded-[9px] ring-1 focus-within:ring-[var(--hairline-focus)] ${
        raised
          ? "bg-[var(--surface)] ring-[var(--hairline-strong)] shadow-lift"
          : "bg-[var(--surface-2)] ring-transparent"
      } ${className}`}
    >
      <Search className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
      <input
        ref={inputRef}
        className="flex-1 min-w-0 self-stretch bg-transparent text-[13px] outline-none placeholder:text-[var(--text-quaternary)]"
        {...rest}
      />
    </div>
  );
}

/** `xs` (20px) sits inside a 28px token or chip: its ×. */
type IconButtonSize = "xs" | "sm" | "md" | "lg";

const ICON_BTN_SIZE: Record<IconButtonSize, string> = {
  xs: "w-5 h-5 rounded-[5px]",
  sm: "w-7 h-7 rounded-[8px]",
  md: "w-8 h-8 rounded-[8px]",
  lg: "w-9 h-9 rounded-lg",
};
const ICON_BTN_ROUND: Record<IconButtonSize, string> = { xs: "w-5 h-5 rounded-full", sm: "w-7 h-7 rounded-full", md: "w-8 h-8 rounded-full", lg: "w-9 h-9 rounded-full" };

/* Square icon-only button. `active` pins the pressed/selected state; `round`
   is for buttons sitting inside a pill, where a squared hover reads as a
   misfit against the container's curve. */
export function IconButton({ size = "sm", active = false, round = false, className = "", children, ...rest }:
  Omit<React.ComponentPropsWithoutRef<"button">, "size"> & { size?: IconButtonSize; active?: boolean; round?: boolean }
) {
  return (
    <button
      className={`press shrink-0 flex items-center justify-center ${(round ? ICON_BTN_ROUND : ICON_BTN_SIZE)[size]} ${
        active
          ? "bg-[var(--accent-active)] text-[var(--text-primary)]"
          : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
      } ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* The one close affordance — always round, everywhere, including inside a row
   of square IconButtons. The round chrome is what marks it as the exit rather
   than one more tool. The × spins on hover of the whole button, not just the
   glyph, so the hit target and the animation agree. */
export function CloseButton({ size = 14, className = "", ...rest }:
  React.ComponentPropsWithoutRef<"button"> & { size?: number }
) {
  const iconRef = useRef<AnimatedIconHandle>(null);
  return (
    <button
      aria-label="Close"
      title="Close"
      className={`press shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)] ${className}`}
      onMouseEnter={() => iconRef.current?.startAnimation()}
      onMouseLeave={() => iconRef.current?.stopAnimation()}
      {...rest}
    >
      <XIcon ref={iconRef} size={size} strokeWidth={1.75} />
    </button>
  );
}

/* Primary action — filled brand. */
export function BrandButton({ className = "", children, ...rest }: React.ComponentPropsWithoutRef<"button">) {
  return (
    <button
      className={`press inline-flex shrink-0 whitespace-nowrap items-center justify-center gap-1.5 h-8 px-3.5 rounded-[10px] text-[13px] font-medium bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)] shadow-button ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* Secondary action — quiet ringed surface. */
export function GhostButton({ className = "", children, ...rest }: React.ComponentPropsWithoutRef<"button">) {
  return (
    <button
      className={`press inline-flex shrink-0 whitespace-nowrap items-center justify-center gap-1.5 h-8 px-3.5 rounded-[10px] text-[13px] font-medium bg-[var(--surface)] text-[var(--text-secondary)] ring-1 ring-[var(--hairline-strong)] shadow-lift hover:bg-[var(--surface-sunk)] hover:text-[var(--text-primary)] ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* A filter that is on, with its × to take it off: the Chats panel's channel, Knowledge's
   type, status and owner filters. */
export function RemovableChip({ removeLabel, onRemove, children }: {
  removeLabel: string;
  onRemove: () => void;
  children?: React.ReactNode;
}) {
  return (
    <span className="max-w-full inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1 rounded-full bg-[var(--surface)] ring-1 ring-[var(--hairline)] text-[12px] text-[var(--text-secondary)]">
      {children}
      <button
        type="button"
        onClick={onRemove}
        title={removeLabel}
        aria-label={removeLabel}
        className="press shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)]"
      >
        <X className="w-3 h-3" strokeWidth={2} />
      </button>
    </span>
  );
}

/* Pill badge for version ids and other one-word status marks. `warn` marks a
   version that landed since the user last looked; `success` marks current. */
const CHIP_TONES = {
  neutral: "bg-[var(--surface-2)] text-[var(--text-secondary)]",
  warn: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
  success: "bg-[var(--success-bg)] text-[var(--success)]",
  danger: "bg-[var(--danger-bg)] text-[var(--danger)]",
  info: "bg-[var(--info-bg)] text-[var(--info-text)]",
};
export function Chip({ tone = "neutral", className = "", children }: {
  tone?: keyof typeof CHIP_TONES;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <span className={`shrink-0 h-5 px-2 rounded-full text-[11px] font-medium flex items-center ${CHIP_TONES[tone]} ${className}`}>
      {children}
    </span>
  );
}

/* Trend line. `baseline="zero"` scales from 0 so the height reads as volume;
   `"min"` scales between min and max so a flat-but-high series still shows its
   shape. Stroke is currentColor — set the tone with a text-* class. */
export function Sparkline({ data, width = 64, height = 18, baseline = "zero", dot = false, className = "" }: {
  data: number[];
  width?: number;
  height?: number;
  baseline?: "zero" | "min";
  dot?: boolean;
  className?: string;
}) {
  const max = Math.max(...data, baseline === "zero" ? 1 : -Infinity);
  const min = baseline === "min" ? Math.min(...data) : 0;
  const span = max - min || 1;
  const pad = 2;
  const step = (width - pad * 2) / Math.max(1, data.length - 1);
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);
  const pts = data.map((v, i) => `${(pad + i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <svg width={width} height={height} className={`block shrink-0 ${className}`} aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      {dot && <circle cx={pad + (data.length - 1) * step} cy={y(data[data.length - 1])} r="2.5" fill="var(--info)" />}
    </svg>
  );
}

/* A 0..1 score. One renderer for the trace inspector and the eval matrix — the
   same number must never look like two different things. Below `target` the bar
   turns warn; there is no green "good" state, because a passing score is the
   expected case and shouting about it costs the failures their contrast. */
export function ScoreBar({ value, target = null, width = 52, className = "" }: {
  value: number | null | undefined;
  target?: number | null;
  width?: number;
  className?: string;
}) {
  if (value == null) return <span className={`font-mono text-[11.5px] text-[var(--text-quaternary)] ${className}`}>—</span>;
  const ok = target == null || value >= target;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span className="h-[4px] rounded-full bg-[var(--surface-2)] overflow-hidden" style={{ width }}>
        <span
          className={`block h-full rounded-full ${ok ? "bg-[var(--success)]" : "bg-[var(--warn)]"}`}
          style={{ width: `${Math.round(value * 100)}%` }}
        />
      </span>
      <span className={`font-mono text-[11.5px] tabular-nums ${ok ? "text-[var(--text-primary)]" : "text-[var(--warn-text)]"}`}>
        {value.toFixed(2)}
      </span>
    </span>
  );
}

/* Canonical header for agent tab pages (Traces is the reference): avatar +
   title + subtitle row with right-aligned actions, then a description — the
   same 22px title as a Settings pane, and space rather than a rule before the
   content. Only rendered through AgentPage — never used directly. */
function AgentPageHeader({ agent, title, subtitle, description, children }: AgentPageProps & { children?: React.ReactNode }) {
  return (
    <>
      {/* Actions drop to their own full-width row below sm so they never
          overlap a long title on phones */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-3 mb-1">
        {agent.definable
          ? <Logo size={30} className="shrink-0 text-[var(--text-primary)]" />
          : <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={32} ring rounded={9} />}
        <div className="min-w-0 flex-1 basis-[180px]">
          <h1 className="text-[19px] sm:text-[22px] leading-[28px] font-semibold tracking-[-0.02em] text-[var(--text-primary)] m-0">
            {title}
          </h1>
          {subtitle && <p className="text-[13px] text-[var(--text-tertiary)] m-0">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto min-w-0">{children}</div>
      </div>
      {description && (
        <p className="text-[13.5px] leading-[1.55] text-[var(--text-tertiary)] mt-3 mb-0 max-w-[600px]">{description}</p>
      )}
      <div className="h-7" />
    </>
  );
}

/* Shell for the agent tab pages (Traces, Knowledge, Tasks, Interfaces,
   Artifacts, …; Settings has its own sidebar): one canonical content column + the shared
   header, so width and header structure can never drift between tabs. Pages
   render this inside their own <main>; dialogs/asides stay outside as siblings. */
export interface AgentPageProps {
  agent: Agent;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

export function AgentPage({ agent, title, subtitle, description, actions, children }: AgentPageProps) {
  return (
    <div className="flex-1 min-w-0 overflow-y-auto">
      <div className="max-w-[880px] mx-auto px-4 md:px-10 pt-6 md:pt-10 pb-16 fade-in-up">
        <AgentPageHeader agent={agent} title={title} subtitle={subtitle} description={description}>
          {actions}
        </AgentPageHeader>
        {children}
      </div>
    </div>
  );
}

/* Page header bar: title on the left, actions right-aligned via children.
   `title` accepts a string (canonical type) or a node (title + count badge). */
export function PageHeader({ title, className = "", children }: {
  title?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    /* Dense headers (Schedule) exceed a phone's width — pan to the actions
       rather than clipping them off-screen. No effect when the row fits. */
    <div className={`h-12 shrink-0 flex items-center gap-2.5 px-5 border-b hairline overflow-x-auto no-scrollbar ${className}`}>
      {typeof title === "string" ? (
        <span className="text-[14px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</span>
      ) : (
        title
      )}
      <div className="flex-1" />
      {children}
    </div>
  );
}
